
import { defineComponent } from "vue";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { settingsdata, getOcZone } from "@/api/settings";
import { getGhlStatus, provisionGhlLocation, listGhlLocations, subscribeGhlPlan } from "@/api/ghl.api.js";
import { validateGhlPassword } from "@/utils/ghlPasswordPolicy.js";
import { getActiveInstance, setActiveInstance } from "@/utils/ghlInstance.js";
import SocialFanoutSettings from "@/views/SocialFanoutSettings.vue";
import TeamContentPublish from "@/views/TeamContentPublish.vue";
import ContentPublishHistory from "@/views/ContentPublishHistory.vue";
import AiSettings from "@/views/AiSettings.vue";
import ProspectsList from "@/views/ProspectsList.vue";
import KnowledgeBases from "@/views/KnowledgeBases.vue";
import UnansweredQuestions from "@/views/UnansweredQuestions.vue";
import AiAgents from "@/views/AiAgents.vue";
import Ai247Explanation from "@/components/ai/Ai247Explanation.vue";
import Ai247InstanceRail from "@/components/ai/Ai247InstanceRail.vue";
import Ai247InstanceDetail from "@/components/ai/Ai247InstanceDetail.vue";
import { isSitesBuilderPreviewUser } from "@/utils/sitesBuilderPreview";

const VALID_TABS = ["overview", "portal", "prospects", "knowledge-bases", "unanswered-questions", "ai-agents", "social", "publish", "history", "ai"];

export default defineComponent({
  name: "ai247-portal",
  components: {
    SocialFanoutSettings,
    TeamContentPublish,
    ContentPublishHistory,
    AiSettings,
    ProspectsList,
    KnowledgeBases,
    UnansweredQuestions,
    AiAgents,
    Ai247Explanation,
    Ai247InstanceRail,
    Ai247InstanceDetail
  },
  data() {
    return {
      loading: true,
      submitting: false,
      refreshing: false,
      subscribing: false,
      statusLoaded: false,
      connected: false,
      pendingApplication: false,
      activeTab: "overview",
      instanceFilter: "all",
      publishCampaignId: 0,
      location: null as any,
      instances: [] as any[],
      pendingActivationIds: [] as string[],
      activeInstanceKey: "",
      loadError: "",
      isFullscreen: false,
      instanceRailOpen: true,
      countries: [] as any[],
      form: {
        businessName: "",
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        confirmPassword: "",
        phone: "",
        address: "",
        city: "",
        state: "",
        postalCode: "",
        country: "US",
        timezone: "UTC",
        acceptedLegal: false
      }
    };
  },
  computed: {
    subscription(): any {
      return (this.location as any)?.subscription || null;
    },
    trialDaysLeft(): number {
      const endsAt = this.subscription?.trialEndsAt;
      if (!endsAt) return 0;
      const msLeft = new Date(endsAt).getTime() - Date.now();
      return Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000)));
    },
    showPlanButtons(): boolean {
      return false;
    },
    showInstanceRail(): boolean {
      return this.instances.length > 0 || this.pendingActivationIds.length > 0;
    },
    canSignup(): boolean {
      if (!this.activeInstanceKey) {
        return false;
      }
      return this.pendingActivationIds.includes(String(this.activeInstanceKey));
    },
    isSitesBuilderPreviewUser(): boolean {
      return isSitesBuilderPreviewUser();
    }
  },
  async mounted() {
    document.addEventListener("fullscreenchange", this.syncFullscreenState);
    this.instanceRailOpen = this.loadInstanceRailOpen();
    await this.initialize();
    this.syncTabFromRoute();
  },
  beforeUnmount() {
    document.removeEventListener("fullscreenchange", this.syncFullscreenState);
    if (this.isFullscreen) {
      this.setFullscreenFallback(false);
    }
  },
  watch: {
    "$route.query.tab"() {
      this.syncTabFromRoute();
    }
  },
  methods: {
    getPortalShellEl(): HTMLElement | null {
      return this.$refs.portalShell as HTMLElement | null;
    },
    syncFullscreenState() {
      const shell = this.getPortalShellEl();
      const nativeActive = Boolean(shell && document.fullscreenElement === shell);
      this.isFullscreen = nativeActive;
      document.body.classList.toggle("ai247-portal-fullscreen-active", nativeActive);
    },
    setFullscreenFallback(active: boolean) {
      this.isFullscreen = active;
      document.body.classList.toggle("ai247-portal-fullscreen-active", active);
    },
    async toggleFullscreen() {
      const shell = this.getPortalShellEl();
      if (!shell) return;

      if (document.fullscreenElement === shell || this.isFullscreen) {
        if (document.fullscreenElement === shell && document.exitFullscreen) {
          await document.exitFullscreen();
          return;
        }
        this.setFullscreenFallback(false);
        return;
      }

      try {
        if (shell.requestFullscreen) {
          await shell.requestFullscreen();
          return;
        }
      } catch {
        // Fall back to fixed overlay below.
      }

      this.setFullscreenFallback(true);
    },
    loadInstanceRailOpen(): boolean {
      try {
        const stored = localStorage.getItem("ai247_instance_rail_open");
        if (stored === null) return true;
        return stored !== "false";
      } catch {
        return true;
      }
    },
    persistInstanceRailOpen(open: boolean) {
      try {
        localStorage.setItem("ai247_instance_rail_open", open ? "true" : "false");
      } catch {
        // ignore storage errors
      }
    },
    setInstanceRailOpen(open: boolean) {
      this.instanceRailOpen = open;
      this.persistInstanceRailOpen(open);
    },
    toggleInstanceRail() {
      this.setInstanceRailOpen(!this.instanceRailOpen);
    },
    normalizeTab(tab: string | null | undefined) {
      const value = String(tab || "overview").toLowerCase();
      if (!VALID_TABS.includes(value)) return "overview";
      return value;
    },
    syncTabFromRoute() {
      const nextTab = this.normalizeTab(this.$route.query.tab as string);
      this.activeTab = nextTab;
      const campaignId = Number(this.$route.query.campaignId || 0);
      this.publishCampaignId = campaignId;
      if (String(this.$route.query.tab || "portal").toLowerCase() !== nextTab) {
        this.$router.replace({
          query: { ...this.$route.query, tab: nextTab }
        });
      }
    },
    onTabChange(tabName: string) {
      if (tabName === "sites-link") {
        this.$router.push({ path: "/sites" });
        return;
      }
      const nextTab = this.normalizeTab(tabName);
      this.activeTab = nextTab;
      this.$router.replace({
        query: { ...this.$route.query, tab: nextTab }
      });
    },
    switchTab(tabName: string) {
      this.onTabChange(tabName);
    },
    editCampaign(id: number) {
      this.publishCampaignId = id;
      this.$router.replace({
        query: { ...this.$route.query, tab: "publish", campaignId: String(id) }
      });
      this.activeTab = "publish";
    },
    onNewPublishPost() {
      this.publishCampaignId = 0;
    },
    formatDate(value: string) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString();
    },
    formatInstanceStatus(status: string) {
      if (status === "suspended") return "Disconnected";
      if (status === "pending_application") return "Pending Application";
      if (status === "expired") return "Expired";
      return status || "unknown";
    },
    instanceStatusClass(status: string) {
      if (status === "active") return "badge-light-success";
      if (status === "pending_application") return "badge-light-warning";
      if (status === "suspended" || status === "expired") return "badge-light-danger";
      return "badge-light-secondary";
    },
    planLabel(plan: string) {
      if (plan === "monthly") return "Monthly plan";
      if (plan === "quarterly") return "Quarterly plan";
      if (plan === "yearly") return "1-year access";
      if (plan === "trial") return "Free trial";
      return plan || "";
    },
    async choosePlan(plan: string) {
      const confirmation = await Swal.fire({
        title: "",
        text: `Subscribe to the ${plan} plan? The fee will be charged to your eWallet.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Subscribe",
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-light"
      });
      if (!confirmation.isConfirmed) return;

      this.subscribing = true;
      try {
        const response = await subscribeGhlPlan({ plan });
        const data = response.data || {};
        if (data.result !== "SUCCESS") {
          throw new Error(data.message || "Subscription failed.");
        }

        await this.loadInstances();
        await this.loadStatus();

        Swal.fire({
          title: "",
          text: "Your AI 24/7 subscription is now active.",
          icon: "success",
          confirmButtonClass: "btn btn-secondary"
        });
      } catch (err) {
        Swal.fire({
          title: "",
          text: err?.response?.data?.message || err?.message || "Subscription failed.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      } finally {
        this.subscribing = false;
      }
    },
    getValidInstanceKeys() {
      const keys = this.instances.map((row: any) => String(row.usid));
      for (const activationId of this.pendingActivationIds) {
        if (!keys.includes(activationId)) {
          keys.push(activationId);
        }
      }
      return keys;
    },
    ensureActiveInstanceSelected() {
      const validKeys = this.getValidInstanceKeys();
      const stored = getActiveInstance();
      if (stored && validKeys.includes(stored)) {
        this.activeInstanceKey = stored;
        setActiveInstance(stored);
        return;
      }

      const dashboardUsid = String(localStorage.getItem("ec_current_usid") || "");
      const dashboardMatch = this.instances.find((row: any) => row.usid === dashboardUsid);
      if (dashboardMatch) {
        this.activeInstanceKey = dashboardMatch.usid;
        setActiveInstance(dashboardMatch.usid);
        return;
      }

      if (this.instances.length) {
        this.activeInstanceKey = this.instances[0].usid;
        setActiveInstance(this.instances[0].usid);
        return;
      }

      if (this.pendingActivationIds.length) {
        this.activeInstanceKey = this.pendingActivationIds[0];
        setActiveInstance(this.pendingActivationIds[0]);
        return;
      }

      this.activeInstanceKey = "";
      setActiveInstance("");
    },
    async selectInstance(instanceKey: string) {
      this.activeInstanceKey = instanceKey;
      setActiveInstance(instanceKey);
      await this.loadStatus();
    },
    startActivateAnother() {
      const nextActivationId = this.pendingActivationIds[0];
      if (!nextActivationId) return;
      this.selectInstance(nextActivationId);
      this.activeTab = "portal";
      this.onTabChange("portal");
    },
    async loadInstances() {
      const response = await listGhlLocations();
      const data = response.data || {};
      this.instances = Array.isArray(data.locations) ? data.locations : [];
      this.pendingActivationIds = Array.isArray(data.pendingActivationIds)
        ? data.pendingActivationIds.map(String)
        : [];
      this.ensureActiveInstanceSelected();
    },
    async initialize() {
      this.loading = true;
      this.loadError = "";
      const errors: string[] = [];

      try {
        await this.loadProfilePrefill();
      } catch (err) {
        errors.push(err?.response?.data?.message || err?.message || "Unable to load profile details.");
      }

      try {
        await this.loadInstances();
        await this.loadStatus();
      } catch (err) {
        errors.push(err?.response?.data?.message || err?.message || "Unable to load AI 24/7 status.");
      }

      this.loadError = errors.join(" ");
      this.loading = false;
    },
    async loadStatus() {
      const response = await getGhlStatus();
      const data = response.data || {};
      this.connected = Boolean(data.connected);
      this.pendingApplication = Boolean(data.pendingApplication);
      this.location = data.location || null;
      this.statusLoaded = true;
    },
    async refreshStatus() {
      this.refreshing = true;
      try {
        await this.loadInstances();
        await this.loadStatus();
        if (this.connected) {
          Swal.fire({
            title: "",
            text: "Your AI 24/7 is now active.",
            icon: "success",
            confirmButtonClass: "btn btn-secondary"
          });
        }
      } catch (err) {
        Swal.fire({
          title: "",
          text: err?.response?.data?.message || err?.message || "Unable to refresh AI 24/7 status.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      } finally {
        this.refreshing = false;
      }
    },
    async loadProfilePrefill() {
      const response = await settingsdata();
      const data = response.data || {};
      const cif = data.cif || {};
      const cifex2 = data.cifex2 || {};
      const countries = data.countries || [];
      this.countries = countries.filter((item: any) => item.iso_code_2);

      this.form.firstName = cif.fname || "";
      this.form.lastName = cif.lname || "";
      this.form.email = cifex2.email || "";
      this.form.phone = cifex2.mobileno || "";
      this.form.address = cifex2.address || "";
      this.form.city = data.city || "";

      const countryId = cifex2.country_id;
      if (countryId) {
        const country = countries.find((item: any) => String(item.country_id) === String(countryId));
        if (country?.iso_code_2) {
          this.form.country = String(country.iso_code_2).toUpperCase();
        }
      }

      if (countryId && cifex2.zone_id) {
        try {
          const zoneResponse = await getOcZone(countryId);
          const zones = zoneResponse.data?.zoneList || [];
          const zone = zones.find((item: any) => String(item.zone_id) === String(cifex2.zone_id));
          if (zone?.name) {
            this.form.state = zone.name;
          }
        } catch {
          // Prefill is best-effort; user can edit state manually.
        }
      }

      const fullName = [this.form.firstName, this.form.lastName].filter(Boolean).join(" ").trim();
      if (fullName) {
        this.form.businessName = fullName;
      }
    },
    validateForm() {
      const required = [
        { key: "firstName", label: "First Name" },
        { key: "lastName", label: "Last Name" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "address", label: "Address" },
        { key: "city", label: "City" },
        { key: "state", label: "State / Region" },
        { key: "postalCode", label: "Postal Code" },
        { key: "country", label: "Country" }
      ];

      for (const field of required) {
        const value = String((this.form as any)[field.key] || "").trim();
        if (!value) {
          Swal.fire({
            title: "",
            text: `${field.label} is required.`,
            icon: "error",
            confirmButtonClass: "btn btn-secondary"
          });
          return false;
        }
      }

      if (!/^[A-Z]{2}$/.test(String(this.form.country || "").trim().toUpperCase())) {
        Swal.fire({
          title: "",
          text: "Country must be a valid 2-letter ISO code (e.g. US, PH).",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
        return false;
      }

      this.form.country = String(this.form.country).trim().toUpperCase();

      const password = String(this.form.password || "");
      const confirmPassword = String(this.form.confirmPassword || "");
      const passwordError = validateGhlPassword(password);
      if (passwordError) {
        Swal.fire({
          title: "",
          text: passwordError,
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
        return false;
      }
      if (password !== confirmPassword) {
        Swal.fire({
          title: "",
          text: "Password and Confirm Password do not match.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
        return false;
      }

      if (!this.form.acceptedLegal) {
        Swal.fire({
          title: "",
          text: "You must agree to the Terms of Service and Privacy Policy.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
        return false;
      }

      return true;
    },
    async submitForm() {
      if (!this.canSignup) {
        Swal.fire({
          title: "",
          text: "You need an AI 24/7 activation to sign up.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
        return;
      }

      if (!this.validateForm()) {
        return;
      }

      this.submitting = true;
      try {
        const { confirmPassword, acceptedLegal, ...provisionPayload } = this.form;
        const response = await provisionGhlLocation(provisionPayload);
        const data = response.data || {};

        if (data.result !== "SUCCESS") {
          throw new Error(data.message || "Failed to create AI 24/7.");
        }

        await this.loadInstances();
        await this.loadStatus();

        let message = "Your AI 24/7 was created successfully.";
        if (data.alreadyExists) {
          message = data.pendingApplication
            ? "Your AI 24/7 is pending application installation."
            : "Your AI 24/7 was already provisioned.";
        } else if (data.pendingApplication) {
          message =
            "Sub-account created. Your portal will activate after an administrator approves the application.";
        } else if (data.status === "active") {
          message = "Your AI 24/7 was created successfully.";
        }

        Swal.fire({
          title: "",
          text: message,
          icon: "success",
          confirmButtonClass: "btn btn-secondary"
        });

        this.form.password = "";
        this.form.confirmPassword = "";
      } catch (err) {
        Swal.fire({
          title: "",
          text: err?.response?.data?.message || err?.message || "Failed to create AI 24/7.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      } finally {
        this.submitting = false;
      }
    }
  }
});
